import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SafetyOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ArrowRightOutlined,
  BankOutlined,
  UserAddOutlined,
  FileAddOutlined,
  SendOutlined,
  MoneyCollectOutlined,
  ShoppingOutlined,
  TeamOutlined,
  TransactionOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  RocketFilled,
  CodeOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  PlusOutlined,
  LinkOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';
import { SEOHead } from '../components/SEO/SEOHead';
import { StructuredData } from '../components/SEO/StructuredData';
import ScrollSmootherComponent from '../components/ScrollSmoother';
import PublicHeader from '../components/landing/PublicHeader';
import PublicFooter from '../components/landing/PublicFooter';
import CtaSection from '../components/landing/CtaSection';
import ProPricing from '../components/landing/ProPricing';
import { useProMessages } from '../i18n/pro';
import PhoneDemo, { PHONE_MESSAGE_COUNT } from '../components/landing/PhoneDemo';
import Reveal from '../components/landing/Reveal';
import CountUp from '../components/landing/CountUp';
import SplitText, { countChars } from '../components/landing/SplitText';
import TypedValue from '../components/landing/TypedValue';
import { useTilt } from '../components/landing/useTilt';
import { gsap, ScrollTrigger, prefersReducedMotion, scrollToElement } from '../components/landing/gsap';
import '../components/landing/landing.css';
import './Home.css';

const stripCheck = (s: string) => s.replace(/^✓\s*/, '');
const stripNumber = (s: string) => s.replace(/^\d+\.\s*/, '');

/* ------------------------------------------------------------------ */
/* Sous-composants                                                     */
/* ------------------------------------------------------------------ */

type FeatureCardProps = {
  index: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
};

const FeatureCard: React.FC<FeatureCardProps> = ({ index, icon, title, description, onClick }) => {
  const { ref, onMouseMove, onMouseLeave } = useTilt<HTMLDivElement>(7);
  return (
    <div
      ref={ref}
      className="ts-feature ts-tilt"
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="ts-tilt-shine" />
      <div className="ts-feature-top">
        <div className="ts-feature-icon">{icon}</div>
        <span className="ts-feature-index">0{index + 1}</span>
      </div>
      <h3 className="ts-feature-title">{title}</h3>
      <p className="ts-feature-desc">{description}</p>
      <span className="ts-feature-arrow">
        <ArrowRightOutlined />
      </span>
    </div>
  );
};

type FaqItemProps = {
  question: string;
  answer: string;
  open: boolean;
  onToggle: () => void;
};

const FaqItem: React.FC<FaqItemProps> = ({ question, answer, open, onToggle }) => (
  <div className={`ts-faq-item ${open ? 'open' : ''}`}>
    <button type="button" className="ts-faq-q" aria-expanded={open} onClick={onToggle}>
      <span>{question}</span>
      <span className="ts-faq-icon">
        <PlusOutlined />
      </span>
    </button>
    <div className="ts-faq-a">
      <div className="ts-faq-a-inner">
        <p>{answer}</p>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const FULL_DEMO = {
  email: 'demo@example.com',
  password: 'secure123',
  amount: '1000',
  invoiceLink: 'https://t.me/stars_paid_bot?start=pay_abc123xyz',
  tonAddress: 'UQDKd9VXyqJZmwLvZ…',
};
const EMPTY_DEMO = { email: '', password: '', amount: '', invoiceLink: '', tonAddress: '' };
const STEP_DURATIONS = [2300, 2700, 2000];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  const heroRef = useRef<HTMLElement | null>(null);
  const demoRef = useRef<HTMLElement | null>(null);
  const demoPhoneRef = useRef<HTMLDivElement | null>(null);
  const demoProgressRef = useRef<HTMLSpanElement | null>(null);
  const howRef = useRef<HTMLElement | null>(null);
  const barRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const timersRef = useRef<number[]>([]);
  const barTweenRef = useRef<any>(null);
  const hasAutoPlayedRef = useRef(false);

  const [visibleMessages, setVisibleMessages] = useState(1);
  const [typing, setTyping] = useState(false);
  const [demoStage, setDemoStage] = useState(0);

  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [demoData, setDemoData] = useState(EMPTY_DEMO);
  const [openFaq, setOpenFaq] = useState<number>(0);

  const goLogin = useCallback(() => navigate('/login'), [navigate]);
  const goPro = useCallback(() => navigate('/login?next=/app/pro'), [navigate]);

  /* ---------- Scroll vers une section demandée (nav/footer) ---------- */
  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    const id = state?.scrollTo || (window.location.hash ? window.location.hash.slice(1) : '');
    if (!id) return;
    const timer = window.setTimeout(() => scrollToElement(id), 450);
    if (state?.scrollTo) {
      navigate(location.pathname, { replace: true, state: null });
    }
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Hero : intro lettre par lettre ---------- */
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      gsap.set(el.querySelectorAll('.ts-hero-anim, .ts-char, .ts-hero-card'), { clearProps: 'all', autoAlpha: 1 });
      return;
    }
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.fromTo('.ts-hero-eyebrow', { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.6 }, 0.15)
        .fromTo(
          '.ts-hero-title .ts-char',
          { yPercent: 110, autoAlpha: 0, rotateX: -50 },
          { yPercent: 0, autoAlpha: 1, rotateX: 0, duration: 0.9, stagger: 0.022, ease: 'power4.out' },
          0.25,
        )
        .fromTo('.ts-hero-subtitle', { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.7 }, '-=0.55')
        .fromTo('.ts-hero-cta > *', { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.1 }, '-=0.45')
        .fromTo('.ts-hero-trust li', { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.08 }, '-=0.4')
        .fromTo(
          '.ts-hero-card-anim',
          { autoAlpha: 0, scale: 0.7, y: 30 },
          { autoAlpha: 1, scale: 1, y: 0, duration: 0.8, stagger: 0.14, ease: 'back.out(1.7)' },
          0.9,
        )
        .fromTo('.ts-scroll-hint', { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.6 }, '-=0.3');
    }, el);
    return () => ctx.revert();
  }, []);

  /* ---------- Démo téléphone pinnée au scroll ---------- */
  useEffect(() => {
    const section = demoRef.current;
    if (!section) return;
    const mm = gsap.matchMedia();

    mm.add('(min-width: 900px) and (prefers-reduced-motion: no-preference)', () => {
      const st = ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: '+=1800',
        pin: true,
        scrub: true,
        anticipatePin: 1,
        onUpdate: (self: any) => {
          const p = self.progress as number;
          const norm = Math.min(1, p / 0.85);
          const seg = norm * (PHONE_MESSAGE_COUNT - 1);
          const count = 1 + Math.min(PHONE_MESSAGE_COUNT - 1, Math.floor(seg));
          const frac = seg - Math.floor(seg);
          setVisibleMessages(count);
          setTyping(count < PHONE_MESSAGE_COUNT && frac > 0.55);
          setDemoStage(count <= 2 ? 0 : count <= 4 ? 1 : 2);
          if (demoProgressRef.current) {
            demoProgressRef.current.style.transform = `scaleX(${norm})`;
          }
          if (demoPhoneRef.current) {
            const ry = (0.5 - p) * 18;
            const rx = (0.5 - p) * 6;
            demoPhoneRef.current.style.transform = `perspective(1400px) rotateY(${ry}deg) rotateX(${rx}deg) translateY(${(0.5 - p) * -30}px)`;
          }
        },
      });
      return () => st.kill();
    });

    mm.add('(max-width: 899px), (prefers-reduced-motion: reduce)', () => {
      let interval: number | null = null;
      const st = ScrollTrigger.create({
        trigger: section,
        start: 'top 70%',
        once: true,
        onEnter: () => {
          let count = 1;
          setVisibleMessages(1);
          interval = window.setInterval(() => {
            count += 1;
            setVisibleMessages(count);
            setDemoStage(count <= 2 ? 0 : count <= 4 ? 1 : 2);
            if (demoProgressRef.current) {
              demoProgressRef.current.style.transform = `scaleX(${(count - 1) / (PHONE_MESSAGE_COUNT - 1)})`;
            }
            if (count >= PHONE_MESSAGE_COUNT && interval) {
              window.clearInterval(interval);
            }
          }, 750);
        },
      });
      return () => {
        if (interval) window.clearInterval(interval);
        st.kill();
      };
    });

    return () => mm.revert();
  }, []);

  /* ---------- Démo interactive "How it works" ---------- */
  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    if (barTweenRef.current) {
      barTweenRef.current.kill();
      barTweenRef.current = null;
    }
  }, []);

  const setBar = useCallback((i: number, pct: number) => {
    const el = barRefs.current[i];
    if (el) el.style.width = `${pct}%`;
  }, []);

  const animateBar = useCallback(
    (i: number, duration: number) => {
      const el = barRefs.current[i];
      if (!el) return;
      el.style.width = '0%';
      barTweenRef.current = gsap.to(el, { width: '100%', duration: duration / 1000, ease: 'none' });
    },
    [],
  );

  const schedule = useCallback((fn: () => void, at: number) => {
    timersRef.current.push(window.setTimeout(fn, at));
  }, []);

  const startDemo = useCallback(() => {
    clearTimers();
    setIsPlaying(true);
    setCurrentStep(0);
    setDemoData(EMPTY_DEMO);
    [0, 1, 2].forEach((i) => setBar(i, 0));

    // Étape 1 : inscription
    animateBar(0, STEP_DURATIONS[0]);
    schedule(() => setDemoData((d) => ({ ...d, email: FULL_DEMO.email })), 300);
    schedule(() => setDemoData((d) => ({ ...d, password: FULL_DEMO.password })), 1300);

    // Étape 2 : lien de paiement
    const t1 = STEP_DURATIONS[0];
    schedule(() => {
      setCurrentStep(1);
      setBar(0, 100);
      animateBar(1, STEP_DURATIONS[1]);
    }, t1);
    schedule(() => setDemoData((d) => ({ ...d, amount: FULL_DEMO.amount })), t1 + 400);
    schedule(() => setDemoData((d) => ({ ...d, invoiceLink: FULL_DEMO.invoiceLink })), t1 + 1200);

    // Étape 3 : retrait
    const t2 = t1 + STEP_DURATIONS[1];
    schedule(() => {
      setCurrentStep(2);
      setBar(1, 100);
      animateBar(2, STEP_DURATIONS[2]);
    }, t2);
    schedule(() => setDemoData((d) => ({ ...d, tonAddress: FULL_DEMO.tonAddress })), t2 + 400);

    const t3 = t2 + STEP_DURATIONS[2];
    schedule(() => {
      setBar(2, 100);
      setIsPlaying(false);
    }, t3);
  }, [animateBar, clearTimers, schedule, setBar]);

  const resetDemo = useCallback(() => {
    clearTimers();
    setIsPlaying(false);
    setCurrentStep(0);
    setDemoData(EMPTY_DEMO);
    [0, 1, 2].forEach((i) => setBar(i, 0));
  }, [clearTimers, setBar]);

  const showStep = useCallback(
    (i: number) => {
      clearTimers();
      setIsPlaying(false);
      setCurrentStep(i);
      setDemoData(FULL_DEMO);
      [0, 1, 2].forEach((k) => setBar(k, k <= i ? 100 : 0));
    },
    [clearTimers, setBar],
  );

  useEffect(() => () => clearTimers(), [clearTimers]);

  // Lancement automatique de la démo quand la section arrive à l'écran
  useEffect(() => {
    const el = howRef.current;
    if (!el) return;
    const st = ScrollTrigger.create({
      trigger: el,
      start: 'top 60%',
      once: true,
      onEnter: () => {
        if (!hasAutoPlayedRef.current) {
          hasAutoPlayedRef.current = true;
          startDemo();
        }
      },
    });
    return () => st.kill();
  }, [startDemo]);

  /* ---------- Données ---------- */
  const features = useMemo(
    () => [
      { icon: <RocketFilled />, title: t.features.instantTitle, description: t.features.instantDesc },
      { icon: <MoneyCollectOutlined />, title: t.features.balanceTitle, description: t.features.balanceDesc },
      { icon: <ShoppingOutlined />, title: t.features.paymentsTitle, description: t.features.paymentsDesc },
      { icon: <SafetyOutlined />, title: t.features.securityTitle, description: t.features.securityDesc },
      { icon: <BankOutlined />, title: t.features.withdrawTitle, description: t.features.withdrawDesc },
      { icon: <CodeOutlined />, title: t.features.apiTitle, description: t.features.apiDesc },
    ],
    [t],
  );

  const stats = [
    { title: t.stats.activeAgents, value: '1000+', icon: <TeamOutlined /> },
    { title: t.stats.transactions, value: '50K+', icon: <TransactionOutlined /> },
    { title: t.stats.uptime, value: '99.9%', icon: <ClockCircleOutlined /> },
    { title: t.stats.support, value: '24/7', icon: <CustomerServiceOutlined /> },
  ];

  const { landing: proLanding } = useProMessages();
  const faqs = [
    { question: t.faq.q1, answer: t.faq.a1 },
    { question: t.faq.q2, answer: t.faq.a2 },
    { question: t.faq.q3, answer: t.faq.a3 },
    { question: t.faq.q4, answer: t.faq.a4 },
    { question: t.faq.q5, answer: t.faq.a5 },
    { question: t.faq.q6, answer: t.faq.a6 },
    ...proLanding.faq,
  ];

  const demoSteps = [
    { title: t.demo.linkCreated, desc: stripCheck(t.how.shareLink) },
    { title: t.demo.confirmPay, desc: t.demo.paymentLabel },
    { title: t.demo.paymentReceived, desc: t.demo.invoiceCredited },
  ];

  const howSteps = [
    { icon: <UserAddOutlined />, title: stripNumber(t.how.step1Title), desc: t.features.instantDesc },
    { icon: <FileAddOutlined />, title: stripNumber(t.how.step2Title), desc: t.features.paymentsDesc },
    { icon: <SendOutlined />, title: stripNumber(t.how.step3Title), desc: t.features.withdrawDesc },
  ];

  const title1Chars = countChars(t.hero.title1);

  return (
    <>
      <SEOHead title={t.seo.title} description={`${t.seo.description} ${proLanding.seoSuffix}`} canonical="/" keywords={t.seo.keywords} />
      <StructuredData type="homepage" />

      <PublicHeader />

      <ScrollSmootherComponent smooth={1.2} effects normalizeScroll>
        <div className="ts-landing ts-home">
          {/* ============================== HERO ============================== */}
          <section className="ts-hero" ref={heroRef}>
            <div className="ts-blob ts-blob--peach ts-hero-blob-1" data-speed="0.85" />
            <div className="ts-blob ts-blob--lilac ts-hero-blob-2" data-speed="1.1" />
            <div className="ts-blob ts-blob--orange ts-hero-blob-3" data-speed="0.95" />

            <div className="ts-hero-inner">
              <span className="ts-eyebrow ts-hero-eyebrow ts-hero-anim">
                <span className="ts-eyebrow-dot" />
                {t.features.paymentsTitle}
              </span>

              <h1 className="ts-hero-title">
                <span className="ts-hero-line">
                  <SplitText text={t.hero.title1} />
                </span>
                <span className="ts-hero-line ts-hero-line--accent">
                  <SplitText text={t.hero.title2} startIndex={title1Chars} />
                </span>
              </h1>

              <p className="ts-hero-subtitle ts-hero-anim">{t.hero.subtitle}</p>

              <div className="ts-hero-cta">
                <button type="button" className="ts-btn ts-btn--primary" onClick={goLogin}>
                  {t.hero.cta}
                  <ArrowRightOutlined />
                </button>
                <button type="button" className="ts-btn ts-btn--ghost" onClick={() => scrollToElement('how-it-works')}>
                  <PlayCircleOutlined />
                  {t.nav.howItWorks}
                </button>
              </div>

              <ul className="ts-hero-trust">
                <li>
                  <CheckCircleOutlined /> {stripCheck(t.how.noCard)}
                </li>
                <li>
                  <CheckCircleOutlined /> {t.how.fee}
                </li>
                <li>
                  <CheckCircleOutlined /> {t.features.withdrawTitle}
                </li>
              </ul>
            </div>

            {/* Cartes flottantes */}
            <div className="ts-hero-card ts-hero-card--a" data-speed="0.9">
              <div className="ts-hero-card-anim">
                <div className="ts-hero-card-inner ts-float">
                  <span className="ts-hero-card-icon ts-hero-card-icon--star">
                    <StarFilled />
                  </span>
                  <div>
                    <strong>+100 {t.demo.stars}</strong>
                    <small>{t.demo.invoiceTitle}</small>
                  </div>
                </div>
              </div>
            </div>
            <div className="ts-hero-card ts-hero-card--b" data-speed="1.08">
              <div className="ts-hero-card-anim">
                <div className="ts-hero-card-inner ts-float--reverse">
                  <span className="ts-hero-card-icon ts-hero-card-icon--ok">
                    <CheckOutlined />
                  </span>
                  <div>
                    <strong>{t.demo.paymentReceived}</strong>
                    <small>{t.demo.invoiceCredited}</small>
                  </div>
                </div>
              </div>
            </div>
            <div className="ts-hero-card ts-hero-card--c" data-speed="0.95">
              <div className="ts-hero-card-anim">
                <div className="ts-hero-card-inner ts-hero-card-inner--link ts-float">
                  <LinkOutlined />
                  <code>t.me/stars_paid_bot?start=pay_xk9m2</code>
                </div>
              </div>
            </div>

            <button type="button" className="ts-scroll-hint" onClick={() => scrollToElement('demo', 0)} aria-label="Scroll">
              <span />
            </button>
          </section>

          {/* ============================== DÉMO TÉLÉPHONE ============================== */}
          <section id="demo" className="ts-demo" ref={demoRef}>
            <div className="ts-demo-grid">
              <div className="ts-demo-copy">
                <span className="ts-eyebrow">
                  <span className="ts-eyebrow-dot" />
                  {t.how.title}
                </span>
                <h2 className="ts-title">{t.features.paymentsTitle}</h2>
                <p className="ts-lead ts-demo-lead">{t.features.paymentsDesc}</p>

                <ol className="ts-demo-steps">
                  {demoSteps.map((s, i) => (
                    <li
                      key={i}
                      className={`ts-demo-step ${demoStage >= i ? 'done' : ''} ${demoStage === i ? 'current' : ''}`}
                    >
                      <span className="ts-demo-step-num">{demoStage > i ? <CheckOutlined /> : i + 1}</span>
                      <div className="ts-demo-step-body">
                        <strong>{s.title}</strong>
                        <span>{s.desc}</span>
                      </div>
                    </li>
                  ))}
                </ol>

                <div className="ts-demo-progress" aria-hidden="true">
                  <span ref={demoProgressRef} />
                </div>
              </div>

              <div className="ts-demo-phone" ref={demoPhoneRef}>
                <div className="ts-demo-phone-glow" />
                <PhoneDemo visible={visibleMessages} typing={typing} />
              </div>
            </div>
          </section>

          {/* ============================== CHIFFRES ============================== */}
          <section id="stats" className="ts-section ts-stats">
            <div className="ts-container">
              <Reveal className="ts-section-header">
                <span className="ts-eyebrow">
                  <span className="ts-eyebrow-dot" />
                  {t.stats.support} · {t.stats.uptime}
                </span>
                <h2 className="ts-title">{t.stats.title}</h2>
                <p className="ts-lead">{t.stats.subtitle}</p>
              </Reveal>
              <Reveal stagger={0.1} className="ts-stats-grid" y={50}>
                {stats.map((s, i) => (
                  <div key={i} className="ts-stat">
                    <div className="ts-stat-icon">{s.icon}</div>
                    <div className="ts-stat-value">
                      <CountUp value={s.value} />
                    </div>
                    <div className="ts-stat-label">{s.title}</div>
                  </div>
                ))}
              </Reveal>
            </div>
          </section>

          {/* ============================== FEATURES ============================== */}
          <section id="features" className="ts-section ts-features">
            <div className="ts-container">
              <Reveal className="ts-section-header">
                <span className="ts-eyebrow ts-eyebrow--light">
                  <span className="ts-eyebrow-dot" />
                  {t.nav.features}
                </span>
                <h2 className="ts-title ts-title--light">{t.features.title}</h2>
                <p className="ts-lead ts-lead--light">{t.features.subtitle}</p>
              </Reveal>
              <Reveal stagger={0.08} className="ts-features-grid" y={60}>
                {features.map((f, i) => (
                  <FeatureCard key={i} index={i} icon={f.icon} title={f.title} description={f.description} onClick={goLogin} />
                ))}
              </Reveal>
            </div>
          </section>

          {/* ============================== HOW IT WORKS (DÉMO INTERACTIVE) ============================== */}
          <section id="how-it-works" className="ts-section ts-how" ref={howRef}>
            <div className="ts-container">
              <Reveal className="ts-section-header">
                <span className="ts-eyebrow">
                  <span className="ts-eyebrow-dot" />
                  {t.how.subtitle}
                </span>
                <h2 className="ts-title">{t.how.title}</h2>
              </Reveal>

              <div className="ts-how-grid">
                <Reveal className="ts-how-steps" stagger={0.1} y={40}>
                  {howSteps.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`ts-how-step ${currentStep === i ? 'current' : ''} ${currentStep > i ? 'done' : ''}`}
                      onClick={() => showStep(i)}
                    >
                      <span className="ts-how-step-num">{currentStep > i ? <CheckOutlined /> : i + 1}</span>
                      <span className="ts-how-step-body">
                        <strong>{s.title}</strong>
                        <span className="ts-how-step-desc">{s.desc}</span>
                        <span className="ts-how-step-bar">
                          <span
                            ref={(el) => {
                              barRefs.current[i] = el;
                            }}
                          />
                        </span>
                      </span>
                      <span className="ts-how-step-icon">{s.icon}</span>
                    </button>
                  ))}

                  <div className="ts-how-controls">
                    <button type="button" className="ts-btn ts-btn--primary ts-how-play" onClick={startDemo} disabled={isPlaying}>
                      <PlayCircleOutlined />
                      {isPlaying ? t.how.demoRunning : t.how.startDemo}
                    </button>
                    <button type="button" className="ts-btn ts-btn--ghost ts-how-reset" onClick={resetDemo}>
                      <ReloadOutlined />
                      {t.how.reset}
                    </button>
                  </div>
                </Reveal>

                <Reveal className="ts-how-screen" y={70}>
                  <div className="ts-window">
                    <div className="ts-window-bar">
                      <span />
                      <span />
                      <span />
                      <div className="ts-window-url">telegram-stars.io/app</div>
                    </div>

                    <div className="ts-window-body" key={currentStep}>
                      {currentStep === 0 && (
                        <div className="ts-panel">
                          <div className="ts-panel-head">
                            <span className="ts-panel-icon">
                              <UserAddOutlined />
                            </span>
                            <h3>{t.how.step1Title}</h3>
                          </div>
                          <label className="ts-field">
                            <span>{t.how.email}</span>
                            <div className={`ts-input ${demoData.email ? 'filled' : ''}`}>
                              {demoData.email ? <TypedValue text={demoData.email} /> : <em>your@email.com</em>}
                            </div>
                          </label>
                          <label className="ts-field">
                            <span>{t.how.password}</span>
                            <div className={`ts-input ${demoData.password ? 'filled' : ''}`}>
                              {demoData.password ? <TypedValue text={demoData.password} mask speed={70} /> : <em>••••••••</em>}
                            </div>
                          </label>
                          <div className="ts-panel-note">
                            <CheckCircleOutlined /> {stripCheck(t.how.noCard)}
                          </div>
                        </div>
                      )}

                      {currentStep === 1 && (
                        <div className="ts-panel">
                          <div className="ts-panel-head">
                            <span className="ts-panel-icon">
                              <FileAddOutlined />
                            </span>
                            <h3>{t.how.step2Title}</h3>
                          </div>
                          <label className="ts-field">
                            <span>{t.how.amountStars}</span>
                            <div className={`ts-input ${demoData.amount ? 'filled' : ''}`}>
                              {demoData.amount ? (
                                <>
                                  <TypedValue text={demoData.amount} speed={90} />
                                  <span className="ts-input-suffix">
                                    <StarFilled /> {t.demo.stars}
                                  </span>
                                </>
                              ) : (
                                <em>1000</em>
                              )}
                            </div>
                          </label>
                          {demoData.invoiceLink && (
                            <div className="ts-link-box">
                              <span className="ts-link-box-label">{t.how.generatedLink}</span>
                              <div className="ts-link-box-value">
                                <LinkOutlined />
                                <code>{demoData.invoiceLink}</code>
                              </div>
                              <span className="ts-link-box-note">
                                <CheckCircleOutlined /> {stripCheck(t.how.shareLink)}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {currentStep === 2 && (
                        <div className="ts-panel">
                          <div className="ts-panel-head">
                            <span className="ts-panel-icon">
                              <SendOutlined />
                            </span>
                            <h3>{t.how.step3Title}</h3>
                          </div>
                          <label className="ts-field">
                            <span>{t.how.tonAddress}</span>
                            <div className={`ts-input ${demoData.tonAddress ? 'filled' : ''}`}>
                              {demoData.tonAddress ? <TypedValue text={demoData.tonAddress} speed={35} /> : <em>UQ...</em>}
                            </div>
                          </label>
                          <div className="ts-balance-box">
                            <div className="ts-balance-row">
                              <span>{t.how.availableBalance}</span>
                              <strong>
                                <CountUp value="9700" duration={1.2} /> {t.demo.stars}
                              </strong>
                            </div>
                            <div className="ts-balance-fee">{t.how.fee}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </section>

          {/* ============================== PRICING ============================== */}
          <section id="pricing" className="ts-section ts-pricing">
            <div className="ts-blob ts-blob--lilac ts-pricing-blob" data-speed="0.9" />
            <div className="ts-container">
              <Reveal className="ts-section-header">
                <span className="ts-eyebrow">
                  <span className="ts-eyebrow-dot" />
                  {t.footer.pricing}
                </span>
                <h2 className="ts-title">{t.pricing.title}</h2>
                <p className="ts-lead">{t.pricing.subtitle}</p>
              </Reveal>
              <Reveal className="ts-pricing-wrap" y={60} scale={0.96}>
                <ProPricing onStartStandard={goLogin} onStartPro={goPro} />
              </Reveal>
            </div>
          </section>

          {/* ============================== FAQ ============================== */}
          <section id="faq" className="ts-section ts-faq">
            <div className="ts-container ts-faq-container">
              <Reveal className="ts-faq-side">
                <span className="ts-eyebrow">
                  <span className="ts-eyebrow-dot" />
                  FAQ
                </span>
                <h2 className="ts-title">{t.faq.title}</h2>
                <p className="ts-lead">{t.faq.subtitle}</p>
              </Reveal>
              <Reveal stagger={0.07} className="ts-faq-list" y={30}>
                {faqs.map((f, i) => (
                  <FaqItem
                    key={i}
                    question={f.question}
                    answer={f.answer}
                    open={openFaq === i}
                    onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                  />
                ))}
              </Reveal>
            </div>
          </section>

          <CtaSection />

          <PublicFooter />
        </div>
      </ScrollSmootherComponent>
    </>
  );
};

export default Home;
